import { use, useCallback } from 'react';
import styled from 'styled-components';
import { Hymn } from '../../../common/src/model.ts';
import BackArrow from '../assets/arrow-previous-left-icon.svg?react';
import InfoIcon from '../assets/info-outline.svg?react';
import ReportIssueIcon from '../assets/tools.svg?react';
import { SongOptions, SongOptionsSetting } from '../pages/settings/song_options.tsx';
import { SequencerContext } from '../player/sequencer.tsx';
import './kebab.css';
import { TransposeKeyRow } from './transpose_key_row.tsx';

interface Props {
  hymn: Hymn;
  displaySongInfo: () => void;
  displayModal: () => void;
  close: () => void;
  initiallyExpandedSetting?: SongOptionsSetting;
}

export const Kebab = ({ displaySongInfo, displayModal, close, hymn, initiallyExpandedSetting }: Props) => {
  // a practice video cannot be transposed (GitHub issue #4041)
  const { videoEnabled } = use(SequencerContext);

  const onReportIssue = useCallback(() => {
    close();
    displayModal();
  }, [close, displayModal]);

  return (
    <KebabWrapper>
      <IconBar>
        <div id={kebabCloseButtonId} onClick={close}>
          <BackArrow className="backArrow" />
        </div>
        <ReportIssueIconWrapper onClick={onReportIssue} />
        <InfoButton onClick={displaySongInfo} />
      </IconBar>
      <PlayerSettings>
        <SongOptions initiallyExpandedSetting={initiallyExpandedSetting} />
        {!videoEnabled && <TransposeKeyRow hymn={hymn} />}
      </PlayerSettings>
    </KebabWrapper>
  );
};

export const kebabCloseButtonId = 'kebab-close-button';

// The height of the icon bar pinned to the top of the song menu sheet, which covers that much of the sheet
// while its rows scroll under it
export const kebabIconBarHeightPx = 50;

// The sheet's width, not the viewport's, so a scrollbar in the sheet cannot push the corner icons under it
const KebabWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ReportIssueIconWrapper = styled(ReportIssueIcon)`
  position: absolute;
  top: 10px;
  right: 50px;
  height: 30px;
  width: 30px;
  color: var(--color-text);
  fill: var(--color-text);
  transform: scale(0.75);
  cursor: pointer;
`;

const InfoButton = styled(InfoIcon)`
  position: absolute;
  top: 10px;
  right: 10px;
  height: 30px;
  width: 30px;
  color: var(--color-text);
  cursor: pointer;
`;

// Pinned to the top of the sheet, so the back arrow and the info icon stay reachable when the sheet scrolls
// (landscape, short screens); the icons are positioned inside it
const IconBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  height: ${kebabIconBarHeightPx}px;
  background-color: var(--color-background);
`;

const PlayerSettings = styled.div`
  margin-left: 5px;
`;
